/**
 * useMinuteClock — "now", by the minute (spec 2026-10-06 §7.6).
 *
 * Re-reads the device clock every MINUTE_CLOCK_POLL_MS while the app is in the foreground (and at
 * once on returning to it — useOptimizedPolling's start), but hands out a new Date only when the
 * minute changes: callers re-render once a minute at most, and a passed time turns late within
 * ~15 s. `override` pins it (the screens' test seams) and nothing polls.
 */
import { useCallback, useState } from 'react';
import { useOptimizedPolling } from '@/hooks/admin/useAppState';

export const MINUTE_CLOCK_POLL_MS = 15_000;

/** Whole minutes since the epoch. Zone offsets are whole minutes, so this flips on local minutes too. */
const minuteOf = (d: Date): number => Math.floor(d.getTime() / 60_000);

export function useMinuteClock(override?: Date): Date {
  const [now, setNow] = useState<Date>(() => override ?? new Date());
  const refresh = useCallback(() => {
    setNow(prev => {
      const next = new Date();
      return minuteOf(next) === minuteOf(prev) ? prev : next;
    });
  }, []);
  useOptimizedPolling(refresh, MINUTE_CLOCK_POLL_MS, !override);
  return override ?? now;
}
